<template>
	<div>
		<v-container>
			<v-row align="end">
				<v-col cols="5">
					<k-card-header title="Transaction Classification Rules" :items="headerItems"></k-card-header>
				</v-col>
				<v-col cols="2">
					<v-btn
						v-if="mappedRules.length < allRules.length"
						@click="onShowNeedsMappingsClick()"
						color="accent"
						:loading="loading"
						:disabled="loading"
						outlined
						ref="needsMappingsButton"
					>
					{{getShowNeedsMappingsLabel()}}
					</v-btn>
				</v-col>
				<v-col cols="4" class="text-right" >
					<span v-if="lastPredictionTime!=null">
						<strong>Last Successful Scoring </strong> {{lastPredictionTime.toLocaleString()}}
					</span>
				</v-col>
			</v-row>

			<v-row>
				<v-col cols="12">
					<v-card class="detailCard">
            			<v-card-text>
							<v-row>
								<v-col cols="12">
									<v-data-table
										:headers="headers"
										:items="rules"
										:loading="loading"
										:footer-props="footerProps"
										:hide-default-footer="false"
										item-key="id"
										:custom-sort="customSort"
										fixed-header
										id="transactionClassificationTable"
										@current-items="onVisibleItemsChanged">
											<template v-slot:item="{ item, index }">
												<tr @click.exact="rowClick(item, index)" @click.ctrl.exact="ctrlClick(item, index)"
													@click.meta.exact="ctrlClick(item, index)" @click.shift.exact="shiftClick(item, index)"
													:class='getRowClassForItem(item)'>
													<td><transaction-classification-rule-dialog :rule="item"
														@success="onRulesSaved" :existingRules="rules"
														:classificationMap="transactionClassifications" smallButton="true"/></td>
													<td>{{ transactionClassifications.get(item.transactionClassification) }}</td>
													<td>{{ item.transactionCodes.join("; ") }}</td>
													<td>{{ item.includePatterns.join("; ") }}</td>
													<td>{{ item.excludePatterns.join("; ") }}</td>
													<td>{{ item.requiresCheckNumber ? "Yes" : "No" }}</td>
													<td>{{ item.dollarAmountFilterType }}</td>
													<td>{{ item.dollarAmountFilter }}</td>
													<td>{{ item.dollarAmountFilterAllowMultiples ? "Yes" : "No" }}</td>
													<td>{{ item.lastQueryCount }}</td>
													<td v-if="item.transactionClassification!=='NEEDS_MAPPING'">{{ getConfidenceLevel(item) }}</td>
													<td v-else><v-icon color="red">fa-user-edit</v-icon></td>
													<td>
														<v-menu offset-y>
															<template v-slot:activator="{ on }">
																<v-btn icon text v-on="on">
																	<v-icon>fa-ellipsis-v</v-icon>
																</v-btn>
															</template>
															<v-list v-if="selectedRules.has(item) && selectedRules.size > 1" id='multiselectmenu'>
																<v-list-item @click="onMergeSelectedRules()">
																	<v-list-item-title>Merge {{selectedRules.size}} Rules</v-list-item-title>
																</v-list-item>
																<v-list-item @click="onDeleteSelectedRules()">
																	<v-list-item-title>Delete {{selectedRules.size}} Rules</v-list-item-title>
																</v-list-item>
															</v-list>
															<v-list v-else>
																<v-list-item @click="onDeleteSingleRule(item)" id='singleselectmenu'>
																	<v-list-item-title>Delete Rule</v-list-item-title>
																</v-list-item>
															</v-list>
														</v-menu>
													</td>
												</tr>
											</template>

											<template v-slot:header.requiresCheckNumber>
												<v-tooltip top>
													<template v-slot:activator="{ on }">
														<v-icon v-on="on">fa-money-check</v-icon>
													</template>
													<span>Requires Check Number?</span>
												</v-tooltip>
											</template>

											<template v-slot:header.dollarAmountFilter>
												<v-tooltip top>
													<template v-slot:activator="{ on }">
														<v-icon v-on="on">fa-funnel-dollar</v-icon>
													</template>
													<span>Dollar Amount Filter</span>
												</v-tooltip>
											</template>

											<template v-slot:header.dollarAmountFilterType>
												<v-tooltip top>
													<template v-slot:activator="{ on }">
														<v-icon v-on="on">fa-filter</v-icon>
													</template>
													<span>Dollar Amount Filter Type</span>
												</v-tooltip>
											</template>

											<template v-slot:header.dollarAmountFilterAllowMultiples>

												<v-tooltip top>
													<template v-slot:activator="{ on }">
														<v-icon v-on="on">fa-layer-group</v-icon>
													</template>
													<span>Dollar Amount Filter Allow Multiples?</span>
												</v-tooltip>
											</template>

											<template v-slot:header.lastQueryCount>
												<v-tooltip top>
													<template v-slot:activator="{ on }">
														<v-icon v-on="on">fa-stopwatch</v-icon>
													</template>
													<span>Last Query Count</span>
												</v-tooltip>
											</template>

											<template v-slot:header.confidence>
												<v-tooltip top>
													<template v-slot:activator="{ on }">
														<v-icon v-on="on">fa-percentage</v-icon>
													</template>
													<span>Confidence Score</span>
												</v-tooltip>
											</template>
									</v-data-table>
								</v-col>
							</v-row>

							<v-row v-if="!loading">
								<v-col>
									<span class="pr-4">
										<transaction-classification-rule-dialog :existingRules="rules"
											:classificationMap="transactionClassifications"
											@success="onRulesSaved"/>
									</span>
									<span class="pr-4" v-if="lastPredictionTime!==null">
										<prediction-summary-dialog :rules="rules" :lastPredictionTime="lastPredictionTime"/>
									</span>
									<span class="pr-4" v-if="rules.length===0">
										<copy-from-fi-dialog @success="loadRules"/>
									</span>
									<span class="pr-4" v-if="rules.length===0">
										<v-btn id="uffImportButton" class="blue white--text" @click="onUffImport">
											Import from uff_transaction.txt files
										</v-btn>
									</span>
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
		</v-container>
	</div>
</template>

<script lang="ts">
import Vue from 'vue';
import { AxiosResponse } from 'axios';
import {
  Component, Watch, Inject, Provide,
} from 'vue-property-decorator';
import {
  TransactionClassificationResource, TransactionClassificationRuleResource, TransactionClassificationRule,
  TransactionClassificationRulePrediction,
} from '@/api/transactionclassification/';
import { ConfigVersion } from '@/api/configversion/';
import TransactionClassificationRuleDialog from '@/components/TransactionClassificationRuleDialog.vue';
import PredictionSummaryDialog from '@/components/PredictionSummaryDialog.vue';
import CopyFromFIDialog from '@/components/CopyFromFIDialog.vue';
import { Alert, NoticeClass, Dialog } from '@kasasa/fbase-components/lib';
import { KCardHeader } from '@kasasa/fbase-components';
import { mapState } from 'vuex';

@Component({
  computed: mapState([
    'fiId', 'configVersion',
  ]),
  components: {
    'transaction-classification-rule-dialog': TransactionClassificationRuleDialog,
    'prediction-summary-dialog': PredictionSummaryDialog,
    'copy-from-fi-dialog': CopyFromFIDialog,
    KCardHeader,
  },
})
export default class TransactionClassification extends Vue {
	fiId!:string;

	configVersion!:ConfigVersion;

	transactionClassifications!:Map<string, string>;

	rules:TransactionClassificationRule[] = [];

	mappedRules:TransactionClassificationRule[] = [];

	allRules:TransactionClassificationRule[] = [];

	selectedRules:Set<TransactionClassificationRule> = new Set<TransactionClassificationRule>();

	// Shift-clicking always works from the last item that was clicked or ctrl-clicked.  If the last was
	// unselected it works from the one before that, etc.  Using a set so they can be pulled out if
	// unselected, but Javascript sets maintain their order, so we can still grab the latest.
	singleSelectionHistory:Set<number> = new Set<number>();

	// If we shift-click a second time, the previous one is undone, so remember it.
	shiftSelectRules:number[] = [];

	visibleRules:TransactionClassificationRule[] = [];

	loading:boolean = true;

	lastPredictionTime:Date|null = null;

	showNeedsMappings: boolean = true;

	headerItems: any = [
	  {
	    text: 'Rewards Configuration',
	    disabled: false,
	    link: false,
	  },
	  {
	    text: 'Transaction Classification Rules',
	    disabled: true,
	  },
	];

	headers:any = [
	  { value: 'edit' },
	  { text: 'Transaction Type', value: 'transactionClassification' },
	  { text: 'Code', value: 'transactionCodes', width: '200px' },
	  { text: 'Include', value: 'includePatterns', width: '100px' },
	  { text: 'Exclude', value: 'excludePatterns', width: '100px' },
	  { text: 'Require Check Number', value: 'requiresCheckNumber', width: '60px' },
	  { text: 'Dollar Amount Filter Type', value: 'dollarAmountFilterType', width: '60px' },
	  { text: 'Dollar Amount Filter', value: 'dollarAmountFilter', width: '60px' },
	  { text: 'Dollar Amount Filter Allow Multiples', value: 'dollarAmountFilterAllowMultiples', width: '60px' },
	  { text: 'Last Query Count', value: 'lastQueryCount', width: '60px' },
	  { text: 'Confidence', value: 'confidence', width: '60px' },
	  { value: 'menu' },
	]

	footerProps: any = {
	  'items-per-page-options': [{ value: -1, text: 'All' }, 25, 50, 100],
	}

	@Inject('TransactionClassificationResource')
	@Provide('TransactionClassificationResource')
	private classificationResource!: TransactionClassificationResource;

	@Inject('TransactionClassificationRuleResource')
	@Provide('TransactionClassificationRuleResource')
	ruleResource!:TransactionClassificationRuleResource;

	mounted() {
	  this.loading = true;
	  this.classificationResource.getAll().then((result:Map<string, string>) => {
	    this.transactionClassifications = result;
	    this.getLastPredictionTime();
	  })
	    .catch((error:any) => {
	      	this.showError(error);
	    });
	}

	@Watch('configVersion')
	getLastPredictionTime() {
	  this.ruleResource.getLastPredictionTime(this.fiId).then((result:Date|null) => {
	    this.lastPredictionTime = result;
	    this.loadRules();
	  })
	    .catch((error:any) => {
	      this.showError(error);
	      this.loading = false;
	    });
	}

	onShowNeedsMappingsClick() {
	  this.showNeedsMappings = !this.showNeedsMappings;
	  this.rules = this.showNeedsMappings ? this.allRules : this.mappedRules;
	}

	loadRules() {
	  this.rules = [];
	  this.mappedRules = [];
	  this.selectedRules.clear();
	  this.loading = true;
	  this.ruleResource.getAll(this.fiId).then((result:TransactionClassificationRule[]) => {
	    const rules:TransactionClassificationRule[] = [];
	    const mappedRules:TransactionClassificationRule[] = [];

	    for (let i:number = 0; i < result.length; i++) {
	      const newEntry:TransactionClassificationRule = Object.assign({}, result[i]) as TransactionClassificationRule;
	      newEntry.isSelected = false;
	      rules.push(newEntry);
	      if (newEntry.transactionClassification !== 'NEEDS_MAPPING') {
	        mappedRules.push(newEntry);
	      }
	    }

	    this.allRules = rules;
	    this.mappedRules = mappedRules;
	    this.rules = rules;
	    this.loading = false;
	  	})
	    .catch((error:any) => {
	      	this.showError(error);
	      	this.loading = false;
	    });
	}

	showError(error:any) {
	  if (error.response && error.response.data && error.response.data.entity) {
	    this.setError(error.response.data.entity);
	  } else {
	    this.setError('An unexpected error occurred.');
	  }
	}

	setError(message:string) {
	  const al = new Alert(message, NoticeClass.ERROR);
	  al.setTimeout(5000);
	  this.$store.dispatch('notices/add', al);
	}

	onRulesSaved(savedRules:TransactionClassificationRule[]) {
	  for (let i:number = 0; i < savedRules.length; i++) {
		  let found:boolean = false;
		  for (let x:number = 0; x < this.rules.length; x++) {
			  if (this.rules[x].id === savedRules[i].id) {
				  // Saved an existing rule, so make sure that it is up to date.
				  Object.assign(this.rules[x], savedRules[i]);
				  found = true;
				  break;
			  }
		  }

		  if (!found) {
			  const rule = savedRules[i];
			  rule.isSelected = false;
			  this.rules.push(rule);
		  }
	  }
	}

	onVisibleItemsChanged(items:TransactionClassificationRule[]) {
	  this.deselectAllRules();
	  this.visibleRules = items;
	}

	rowClick(rule:TransactionClassificationRule, index:number) {
	  this.deselectAllRules();
	  this.shiftSelectRules = [];
	  this.selectRule(rule);
	  this.singleSelectionHistory.add(index);
	}

	ctrlClick(rule:TransactionClassificationRule, index:number) {
	  this.shiftSelectRules = [];
	  if (!this.selectedRules.has(rule)) {
	    this.selectRule(rule);
	    this.singleSelectionHistory.add(index);
	  } else {
	    this.deselectRule(rule, index);
	  }
	}

	shiftClick(rule:TransactionClassificationRule, index:number) {
	  this.shiftSelectRules.forEach(deselectIndex => this.deselectRule(this.visibleRules[deselectIndex], deselectIndex));
	  this.shiftSelectRules = [];

	  let startIndex:number = -1;
	  if (this.singleSelectionHistory.size > 0) {
	    startIndex = Array.from(this.singleSelectionHistory).pop() as number;
	  }

	  let step:number = 1;
	  if (startIndex > index) {
	    step = -1;
	  }

	  for (let i:number = (startIndex + step); i !== (index + step); i += step) {
	    this.selectRule(this.visibleRules[i]);
	    this.shiftSelectRules.push(i);
	  }
	}

	selectRule(rule:TransactionClassificationRule) {
	  this.selectedRules.add(rule);
	  /* eslint-disable no-param-reassign */
	  rule.isSelected = true;
	  /* eslint-enable no-param-reassign */
	}

	deselectRule(rule:TransactionClassificationRule, index:number) {
	  this.selectedRules.delete(rule);
	  this.singleSelectionHistory.delete(index);
	  /* eslint-disable no-param-reassign */
	  rule.isSelected = false;
	  /* eslint-enable no-param-reassign */
	}

	deselectAllRules() {
	  this.selectedRules.forEach((rule) => {
	    /* eslint-disable no-param-reassign */
	  	rule.isSelected = false;
	  	/* eslint-enable no-param-reassign */
	  });

	  this.selectedRules.clear();
	  this.singleSelectionHistory.clear();
	  this.shiftSelectRules = [];
	}

	onDeleteSingleRule(rule:TransactionClassificationRule) {
	  const that = this;
	  const dialog = new
	  Dialog('Confirm Deletion', 'Are you sure you want to delete this transaction classification rule?');
	  dialog.setAcceptLabel('OK')
	    .setDeclineLabel('Cancel');
	  this.$store.dispatch('notices/add', dialog)
	    .then((ok) => {
	      if (ok === 'accept') {
	        that.ruleResource.remove(that.fiId, rule.id as number).then((result:AxiosResponse) => {
	          that.removeRuleFromList(rule);
	          that.selectedRules.delete(rule);
	        });
	      }
	    });
	}

	onDeleteSelectedRules() {
	  	const that = this;
	  const dialog = new Dialog('Confirm Deletion', 'Are you sure you want to delete these '
			+ `${this.selectedRules.size} transaction classification rules?`);
	  dialog.setAcceptLabel('OK')
	    .setDeclineLabel('Cancel');
	  this.$store.dispatch('notices/add', dialog)
	    .then((ok) => {
	      if (ok === 'accept') {
	        that.deleteSelectedRules();
	      }
	    });
	}

	async deleteSelectedRules():Promise<void> {
	  const toDeleteArray:TransactionClassificationRule[] = [...this.selectedRules];
	  const results:Promise<AxiosResponse<any>>[] = [];
	  	for (let i:number = 0; i < toDeleteArray.length; i++) {
	    results.push(this.ruleResource.remove(this.fiId, toDeleteArray[i].id as number));
	    	this.removeRuleFromList(toDeleteArray[i]);
	    	this.selectedRules.delete(toDeleteArray[i]);
	  	}
	  await Promise.all(results);
	}

	removeRuleFromList(rule:TransactionClassificationRule) {
	  for (let i:number = 0; i < this.rules.length; i++) {
	    if (this.rules[i].id === rule.id) {
	      this.rules.splice(i, 1);
	      break;
	    }
	  }
	}

	onMergeSelectedRules() {
	  if (!this.allSelectedRuleHaveSameTransactionType()) {
	    this.setError('The selected rules could not be merged because they have different transaction types.');
	  } else {
	    const ruleIds:number[] = [];
	    this.selectedRules.forEach((rule) => {
	      ruleIds.push(rule.id as number);
	    });
	    this.ruleResource.mergeRules(this.fiId, ruleIds).then((result:AxiosResponse) => {
	      this.loadRules();
	    })
	      .catch((error:any) => {
	        this.showError(error);
	      });
	  }
	}

	getShowNeedsMappingsLabel() {
	  return `${this.showNeedsMappings ? 'Hide' : 'Show'} Needs Mapping`;
	}

	allSelectedRuleHaveSameTransactionType() : boolean {
	  let sameType:boolean = true;
	  const ruleArray:TransactionClassificationRule[] = [...this.selectedRules];
	  const expectedType:string = ruleArray[0].transactionClassification;
	  for (let i = 1; i < ruleArray.length; i++) {
	    if (ruleArray[i].transactionClassification !== expectedType) {
	      sameType = false;
	      break;
	    }
	  }
	  return sameType;
	}

	getConfidenceLevel = (rule : TransactionClassificationRule) : string => {
	  let confidence = '--';

	  // Show the confidence percentage only if the rule has the highest ranked classification selected
	  const prediction:TransactionClassificationRulePrediction | null = TransactionClassification
	  	.getHighestRankedPrediction(rule);
	  if (prediction !== null && prediction.transactionClassification === rule.transactionClassification) {
	    confidence = Math.trunc(((prediction as TransactionClassificationRulePrediction).confidence * 100)).toString();
	  }

	  return confidence;
	}

	static getHighestRankedPrediction(rule : TransactionClassificationRule)
		: TransactionClassificationRulePrediction | null {
	  let bestPrediction:TransactionClassificationRulePrediction | null = null;

	  if (rule.predictions) {
	    for (let i:number = 0; i < rule.predictions.length; i++) {
	      if (bestPrediction === null || rule.predictions[i].confidence
					> (bestPrediction as TransactionClassificationRulePrediction).confidence) {
	        bestPrediction = rule.predictions[i];
	      }
	    }
	  }

	  return bestPrediction;
	}

	customSort = (items:TransactionClassificationRule[], sortBy:string[],
	  sortDesc:boolean[]) : TransactionClassificationRule[] => {
	  items.sort((a:any, b:any) => {
	    let sortVal:number = 0;
	    // NEEDS_MAPPING always bubbles to the top, no matter what.
	    if (a.transactionClassification === 'NEEDS_MAPPING' && b.transactionClassification !== 'NEEDS_MAPPING') {
	      sortVal = -1;
	    } else if (a.transactionClassification !== 'NEEDS_MAPPING' && b.transactionClassification === 'NEEDS_MAPPING') {
	      sortVal = 1;
	    } else if (sortBy) {
	      for (let i:number = 0; i < sortBy.length; i++) {
	        if (a[sortBy[i]] !== b[sortBy[i]]) {
	          if (sortDesc[i]) {
	            sortVal = a[sortBy[i]] < b[sortBy[i]] ? 1 : -1;
	          } else {
	            sortVal = a[sortBy[i]] < b[sortBy[i]] ? -1 : 1;
	          }
	          break;
	        } else {
	          sortVal = 0;
	        }
	      }
	    }
	    return sortVal;
	  });
	  return items;
	}

	getRowClassForItem = (item:TransactionClassificationRule) : string => {
	  let className:string = '';
	  if (item.isSelected) {
	    if (item.transactionClassification === 'NEEDS_MAPPING') {
	      className = 'needsMappingRow_selected';
	    } else {
	      className = 'selectedRow';
	    }
	  } else if (item.transactionClassification === 'NEEDS_MAPPING') {
	    className = 'needsMappingRow_unselected';
	  }
	  return className;
	}

	onUffImport() {
	  const that = this;
	  const dialog = new Dialog('Confirm Import', 'Are you sure you want to import all transaction '
		  + 'classification rules from uff_transaction.txt files?');
	  dialog.setAcceptLabel('OK')
	    .setDeclineLabel('Cancel');
	  this.$store.dispatch('notices/add', dialog)
	    .then((ok) => {
	      if (ok === 'accept') {
	    			that.ruleResource.importFromUff(that.fiId).then((result:AxiosResponse) => {
	          that.loadRules();
	    			}).catch((error:any) => {
	          that.showError(error);
	        });
	      }
	    });
	}
}
</script>

<style lang="scss">
tbody tr:nth-of-type(odd) {
   background-color: rgba(31, 25, 25, 0.02);
   background-blend-mode: multiply;
 }

 td {
	 user-select:none;	// So that shift-clicking doesn't highlight text
 }

 .selectedRow {
    background: #c7c7c7 !important;
	background-blend-mode: multiply;
  }

.needsMappingRow_unselected {
	background: #FFEBEC !important;
}

.needsMappingRow_selected {
	background: #FF9988 !important;
}

#transactionClassificationTable .v-data-table__wrapper{height:calc(100vh - 400px) !important;}

</style>
